import React, { FormEventHandler, JSX, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuTrash2 } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { Box, BoxProps, Text } from '@chakra-ui/react'

import { UpdateOrder } from 'appTypes/orders'
import { Input } from 'components/form/Input'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { AppDispatch } from 'store'
import { updateCart } from 'store/partials/cart/cartSlice'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { STORAGE_KEYS } from 'utils/constants'
import { getFromStorage } from 'utils/storage'

import { ApplyButton, DeleteButton } from './styles'
import { DiscountCodeValidationError } from '@leid/api-orders'

interface Props extends BoxProps {
  defaultValue?: string
}

export const DiscountCodeBox = ({ defaultValue = '', ...boxProps }: Props): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>()
  const [lastCheckedDiscountCode, setLastCheckedDiscountCode] = useState(defaultValue)
  const [discountCode, setDiscountCode] = useState(defaultValue)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation(['cart', 'common'])

  const isDelete: boolean = useMemo(
    () => Boolean(lastCheckedDiscountCode.trim()) && lastCheckedDiscountCode === discountCode,
    [lastCheckedDiscountCode, discountCode],
  )

  const handleUpdatePromoCode: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    const orderId = Number(getFromStorage(STORAGE_KEYS.orderId))
    setIsLoading(true)

    // do not send a request if code did not change
    if (!isDelete) {
      const serviceCodeResponse = await apiClient.getDiscountCode(discountCode)
      if (!serviceCodeResponse.success || !serviceCodeResponse.data) {
        console.debug('getDiscountCode request returned following error:', serviceCodeResponse)
        dispatch(displaySnackbar({ message: t('common:discountCodeInvalid'), isError: true }))
        setDiscountCode('')
        setIsLoading(false)
        return
      }

      const updatePayload: UpdateOrder = {
        discountCodeId: serviceCodeResponse.data.id,
      }

      if (updatePayload.discountCodeId) {
        const result = await dispatch(updateCart(orderId, updatePayload))

        if (result) {
          if (result.order) {
            dispatch(displaySnackbar({ message: t('common:discountCodeApplied') }))
            setLastCheckedDiscountCode(discountCode)
          }
          if (result.errorCode) {
            switch (result.errorCode) {
              case DiscountCodeValidationError.NOT_FOUND:
                dispatch(displaySnackbar({ message: t('common:discountCode.notFound'), isError: true }))
                break
              case DiscountCodeValidationError.CODE_INACTIVE:
                dispatch(displaySnackbar({ message: t('common:discountCode.inactive'), isError: true }))
                break
              case DiscountCodeValidationError.CODE_EXPIRED:
                dispatch(displaySnackbar({ message: t('common:discountCode.expired'), isError: true }))
                break
              case DiscountCodeValidationError.ALREADY_USED:
                dispatch(displaySnackbar({ message: t('common:discountCode.used'), isError: true }))
                break
              default:
                dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
                break
            }
          }
        }

        setDiscountCode('')
        setLastCheckedDiscountCode('')
      }
    }

    setIsLoading(false)
  }

  const handleDeletePromoCode = async () => {
    const orderId = Number(getFromStorage(STORAGE_KEYS.orderId))

    try {
      const updatePayload: UpdateOrder = {
        discountCodeId: null,
      }

      await dispatch(updateCart(orderId, updatePayload))

      setDiscountCode('')
      setLastCheckedDiscountCode('')
    } catch (error) {
      console.debug('updateOrder request returned following error:', error)
      dispatch(displaySnackbar({ message: t('common:error'), isError: true }))
    }
  }

  return (
    <Box {...boxProps}>
      <form onSubmit={handleUpdatePromoCode}>
        <Text fontWeight={400}>{t('cart:discountCodeHeading')}</Text>
        <Box position="relative" marginTop="26px">
          <Input
            placeholder={t('cart:enterDiscountCode')}
            disabled={isLoading}
            value={discountCode}
            onChange={(val) => setDiscountCode(val.target.value)}
            name="discountCode"
            paddingLeft="18px"
            adornmentRight={
              isDelete ? (
                <DeleteButton onClick={handleDeletePromoCode}>
                  <LuTrash2 size={20} />
                </DeleteButton>
              ) : (
                <ApplyButton type="submit" disabled={!discountCode || isLoading}>
                  {isLoading ? (
                    <Loader isLight size="md" />
                  ) : (
                    <Text color="white" fontSize="sm">
                      {t('common:redeem')}
                    </Text>
                  )}
                </ApplyButton>
              )
            }
          />
        </Box>
      </form>
    </Box>
  )
}

export type DiscountCodeBoxSuccessHandler = (discountCode: string) => void
